import {commitMutation, ConnectionHandler, graphql} from 'react-relay'
import type {Environment, PayloadError, RecordSourceProxy} from 'relay-runtime'

import {BLOCKED_BY_LIST_ALL_RELAY_CONNECTION} from '../components/sections/relations-section/LazyRelationshipsBlockedByListView'
import {BLOCKING_LIST_ALL_RELAY_CONNECTION} from '../components/sections/relations-section/LazyRelationshipsBlockingListView'
import type {
  RemoveBlockedByInput,
  removeBlockedByMutation,
  removeBlockedByMutation$data,
} from './__generated__/removeBlockedByMutation.graphql'

function optimisticRemoveBlockedBy(
  store: RecordSourceProxy,
  input: RemoveBlockedByInput,
  direction: 'blockedBy' | 'blocking',
) {
  // Determine which issue record to update based on the direction:
  // - blockedBy: update the issueId's topBlockedBy connection
  // - blocking: update the blockingIssueId's topBlocking connection
  const viewerIssueId = direction === 'blockedBy' ? input.issueId : input.blockingIssueId
  const dependencyIssueId = direction === 'blockedBy' ? input.blockingIssueId : input.issueId

  const issueRecord = store.get(viewerIssueId)
  if (!issueRecord) return

  // Remove the dependency issue from the appropriate connection
  const connectionField = direction === 'blockedBy' ? 'blockedBy' : 'blocking'
  const connection = issueRecord.getLinkedRecord(connectionField, {first: 3, ranked: true})
  if (connection) {
    const existingNodes = connection.getLinkedRecords('nodes') || []
    const filteredNodes = existingNodes.filter(node => node?.getDataID() !== dependencyIssueId)
    connection.setLinkedRecords(filteredNodes, 'nodes')
  }

  // Update issueDependenciesSummary
  const summary = issueRecord.getLinkedRecord('issueDependenciesSummary')
  if (summary) {
    const currentCount = (summary.getValue(connectionField) as number) || 0
    summary.setValue(Math.max(0, currentCount - 1), connectionField)
  }
}

export function removeBlockedByMutation({
  environment,
  input,
  onError,
  onCompleted,
  optimisticDirection,
}: {
  environment: Environment
  input: RemoveBlockedByInput
  onError?: (error: Error) => void
  onCompleted?: (response: removeBlockedByMutation$data, errors?: readonly PayloadError[] | null) => void
  optimisticDirection?: 'blockedBy' | 'blocking'
}) {
  return commitMutation<removeBlockedByMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation removeBlockedByMutation($input: RemoveBlockedByInput!) @raw_response_type {
        removeBlockedBy(input: $input) {
          issue {
            # Inlined from the former RelationshipsSectionFragment to keep the Relay store fresh.
            id
            databaseId
            viewerCanUpdateMetadata
            repository {
              nameWithOwner
              isArchived
              owner {
                login
              }
            }
            parent {
              id
              titleHTML
              url
              number
              repository {
                nameWithOwner
              }
              state
              stateReason
              subIssuesSummary {
                total
                completed
              }
            }
            topBlockedBy: blockedBy(first: 3, ranked: true) {
              nodes {
                id
                title
                titleHTML
                url
                number
                repository {
                  nameWithOwner
                  name
                  owner {
                    login
                  }
                }
                state
                stateReason
              }
              pageInfo {
                hasNextPage
              }
            }
            topBlocking: blocking(first: 3, ranked: true) {
              nodes {
                id
                title
                titleHTML
                url
                number
                repository {
                  nameWithOwner
                  name
                  owner {
                    login
                  }
                }
                state
                stateReason
              }
              pageInfo {
                hasNextPage
              }
            }
            securityAlerts(first: 100) {
              nodes {
                number
                repository {
                  nameWithOwner
                }
                status
                title
              }
            }
            issueDependenciesSummary {
              blockedBy
              blocking
            }
          }
          blockingIssue {
            id
            number
            title
            # Inlined from the former RelationshipsSectionFragment to keep the Relay store fresh.
            databaseId
            viewerCanUpdateMetadata
            repository {
              nameWithOwner
              isArchived
              owner {
                login
              }
            }
            parent {
              id
              titleHTML
              url
              number
              repository {
                nameWithOwner
              }
              state
              stateReason
              subIssuesSummary {
                total
                completed
              }
            }
            topBlockedBy: blockedBy(first: 3, ranked: true) {
              nodes {
                id
                title
                titleHTML
                url
                number
                repository {
                  nameWithOwner
                  name
                  owner {
                    login
                  }
                }
                state
                stateReason
              }
              pageInfo {
                hasNextPage
              }
            }
            topBlocking: blocking(first: 3, ranked: true) {
              nodes {
                id
                title
                titleHTML
                url
                number
                repository {
                  nameWithOwner
                  name
                  owner {
                    login
                  }
                }
                state
                stateReason
              }
              pageInfo {
                hasNextPage
              }
            }
            securityAlerts(first: 100) {
              nodes {
                number
                repository {
                  nameWithOwner
                }
                status
                title
              }
            }
            issueDependenciesSummary {
              blockedBy
              blocking
            }
          }
        }
      }
    `,
    variables: {
      input,
    },
    optimisticUpdater: optimisticDirection
      ? store => optimisticRemoveBlockedBy(store, input, optimisticDirection)
      : undefined,
    onError: error => onError && onError(error),
    onCompleted: (response, errors) => onCompleted && onCompleted(response, errors),
    updater: store => {
      store.get(input.issueId)?.getLinkedRecord('blockedBy', {first: 100})?.invalidateRecord()
      store.get(input.blockingIssueId)?.getLinkedRecord('blocking', {first: 100})?.invalidateRecord()

      const blockedByDataId = ConnectionHandler.getConnectionID(input.issueId, BLOCKED_BY_LIST_ALL_RELAY_CONNECTION)
      store.get(blockedByDataId)?.invalidateRecord()

      const blockingDataId = ConnectionHandler.getConnectionID(
        input.blockingIssueId,
        BLOCKING_LIST_ALL_RELAY_CONNECTION,
      )
      store.get(blockingDataId)?.invalidateRecord()
    },
  })
}
